import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import type { Editor } from "@tiptap/react";
import { Typography } from "antd";
import { cn } from "@/lib/utils";
import { colors } from "../../theme/colors";
import type { RichTextEditorProps } from "./types";
import { getExtensions } from "./extensions";
import { Toolbar } from "./toolbar";
import { sanitizeHtml, debounce, countWords, getTextFromHtml } from "./utils";
import { useEditorMediaUpload } from "./hooks/useEditorMediaUpload";
import "./styles.css";

const { Text } = Typography;

const sizeConfig = {
  small: { fontSize: '0.875rem', padding: 10 },
  medium: { fontSize: '0.9375rem', padding: 12 },
  large: { fontSize: '1rem', padding: 16 },
} as const;

export const RichTextEditor = ({
  value,
  onChange,
  placeholder = "Start writing...",
  disabled = false,
  maxLength,
  minHeight = 300,
  maxHeight,
  autoFocus = false,
  readOnly = false,
  showWordCount = false,
  showCharCount = false,
  className = "",
  mentionUsers = [],
  label,
  required = false,
  helperText,
  error,
  size = "medium",
  variant = "filled",
  fullWidth = true,
}: RichTextEditorProps) => {
  // Editor ref for the media upload hook
  const editorRef = useRef<Editor | null>(null);
  const { handleMediaUpload, handleMediaDelete } = useEditorMediaUpload(editorRef);

  // Always use the built-in hook for object storage uploads (instant blob preview + background upload).
  // External onMediaUpload/onImageUpload props are ignored — the hook handles everything.
  const uploadHandler = handleMediaUpload;

  // Track pending internal editor updates to prevent value sync from reverting them
  const pendingUpdate = useRef(false);

  // Track the last HTML we emitted to the parent via onChange
  // This prevents setContent from firing when the parent echoes back the same sanitized value
  const lastEmittedHtml = useRef<string>(value);

  // Stable ref for onChange so the debounced handler never needs recreation
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // State to force toolbar re-render on editor state changes
  const [, setEditorState] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const forceToolbarUpdate = useCallback(() => {
    setEditorState((prev) => prev + 1);
  }, []);

  const isFilled = Boolean(value && value.length > 0 && value !== "<p></p>");

  // Border color - same logic as RichTextArea
  const getBorderColor = () => {
    if (variant === "borderless") return "transparent";
    if (error) return colors.error;
    if (isFocused) return colors.primary;
    if (isFilled) return colors.surfaceDark;
    return colors.surfaceSubtle;
  };

  // Background color - same logic as RichTextArea
  const getBackgroundColor = () => {
    if (variant === "borderless") return "transparent";
    if (disabled) return colors.surfaceLighter;
    if (isFocused) return colors.white;
    return variant === "filled" ? colors.surfaceLighter : colors.white;
  };

  // Border width - same logic as RichTextArea
  const getBorderWidth = () => {
    if (variant === "borderless") return 0;
    return isFocused || error ? 2 : 1;
  };

  const config = sizeConfig[size];

  // Memoize extensions to prevent recreation on every render
  const extensions = useMemo(
    () => getExtensions({ placeholder, maxLength, uploadFn: uploadHandler, onMediaDelete: handleMediaDelete, mentionUsers }),
    [placeholder, maxLength, uploadHandler, handleMediaDelete, mentionUsers],
  );

  // Debounced onChange handler — created in effect to avoid ref access during render
  const debouncedOnChangeRef = useRef<((html: string, json: object) => void) | null>(null);
  useEffect(() => {
    debouncedOnChangeRef.current = debounce((html: string, json: object) => {
      const sanitized = sanitizeHtml(html);
      lastEmittedHtml.current = sanitized;
      onChangeRef.current(sanitized, json);
      pendingUpdate.current = false;
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editor = useEditor({
    extensions,
    content: value,
    editable: !disabled && !readOnly,
    autofocus: autoFocus,
    onUpdate: ({ editor }) => {
      pendingUpdate.current = true;
      const html = editor.getHTML();
      const json = editor.getJSON();
      debouncedOnChangeRef.current?.(html, json);
      forceToolbarUpdate();
    },
    onSelectionUpdate: () => {
      forceToolbarUpdate();
    },
    onTransaction: () => {
      forceToolbarUpdate();
    },
    onFocus: () => {
      setIsFocused(true);
    },
    onBlur: () => {
      setIsFocused(false);
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl focus:outline-none",
      },
    },
  });

  // Keep editor ref in sync for the media upload hook
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Update editor content when value prop changes externally
  // Skip if the editor has pending internal changes that haven't propagated through debounced onChange
  // Compare against lastEmittedHtml to avoid setContent when parent echoes back what we sent
  useEffect(() => {
    if (editor && value !== lastEmittedHtml.current && !pendingUpdate.current) {
      editor.commands.setContent(value, false);
      lastEmittedHtml.current = value;
    }
  }, [value, editor]);

  // Update editable state when disabled/readOnly changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled && !readOnly);
    }
  }, [disabled, readOnly, editor]);

  // Character count from the editor's character count extension
  const characterCount = editor?.storage.characterCount?.characters() ?? 0;
  const wordCount = editor ? countWords(getTextFromHtml(editor.getHTML())) : 0;

  // Container style - matches RichTextArea exactly
  const containerStyle: React.CSSProperties = {
    borderColor: getBorderColor(),
    borderWidth: getBorderWidth(),
    borderStyle: "solid",
    backgroundColor: getBackgroundColor(),
    borderRadius: 3,
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    overflow: "hidden",
  };

  const editorContentStyle: React.CSSProperties = {
    minHeight,
    ...(maxHeight ? { maxHeight, overflowY: "auto" as const } : {}),
    fontSize: config.fontSize,
    padding: config.padding,
  };

  return (
    <div className={cn(fullWidth && "w-full", className)}>
      {label && (
        <label
          className="inline-flex items-center gap-1 mb-1.5 text-sm font-medium"
          style={{ color: colors.textDefault }}
        >
          <span>{label}</span>
          {required && <span style={{ color: colors.error }}>*</span>}
        </label>
      )}

      <div
        className={cn(
          "rich-text-editor",
          disabled && "disabled",
          readOnly && "readonly"
        )}
        style={containerStyle}
      >
        {/* Toolbar - hidden in readOnly mode */}
        {!readOnly && (
          <Toolbar
            editor={editor}
            disabled={disabled}
            onMediaUpload={uploadHandler}
          />
        )}

        {/* Editor Content */}
        <div className="rich-text-editor-content" style={editorContentStyle}>
          <EditorContent editor={editor} />
        </div>

        {/* Footer with counts */}
        {(showWordCount || showCharCount) && (
          <div className="rich-text-editor-footer">
            {showWordCount && (
              <div className="count-item">
                <Text type="secondary">{wordCount} words</Text>
              </div>
            )}
            {showCharCount && (
              <div className="count-item">
                <Text type="secondary">
                  {characterCount}
                  {maxLength ? ` / ${maxLength}` : ""} characters
                </Text>
              </div>
            )}
          </div>
        )}
      </div>

      {helperText && !error && (
        <div className="mt-1 text-xs" style={{ color: colors.textMuted }}>
          {helperText}
        </div>
      )}
      {error && (
        <div className="mt-1 text-xs" style={{ color: colors.error }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
